import { Box, Stack, Text } from '@atlaskit/primitives';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { useDialog } from '@databases/dialog';
import {
  DatabaseTraceAction,
  DatabaseTraceSource,
  traceDatabaseAction
} from '@databases/frontend-metrics/perf-tracing';
import { messages } from '@databases/intl/messages';
import { type PanelManager } from '@databases/panel-types';
import { doDeleteFieldsWithIds } from '@databases/redux/contextual-menu/actions';
import { appStore } from '@databases/redux/store';
import { type SelectionsApiContract } from '@databases/selections-api-types';
import { type DatabaseFieldType } from '@databases/types/database';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { MetricName, ReliabilityMetrics } from '@canvas/frontend-metrics';

type UseDeleteFieldsActionProps = {
  selectionsApi: SelectionsApiContract;
  panelManager: PanelManager;
  contentId: string;
};

/**
 * Resolves unique field IDs and names from the current selection.
 * Supports both column mode (direct field selection) and cell mode
 * (fields inferred from selected cell values).
 */
function resolveSelectedFieldInfo(
  resolved: ReturnType<SelectionsApiContract['getResolvedSelection']>
): { fieldIds: string[]; fieldNames: string[]; fieldTypes: DatabaseFieldType[] } {
  if (resolved.mode === 'column') {
    return {
      fieldIds: resolved.fields.map((f) => f.fieldId),
      fieldNames: resolved.fields.map((f) => f.field.name),
      fieldTypes: resolved.fields.map((f) => f.field.type)
    };
  }

  if (resolved.mode === 'cell') {
    const seen = new Set<string>();
    const fieldIds: string[] = [];
    const fieldNames: string[] = [];
    const fieldTypes: DatabaseFieldType[] = [];

    for (const value of resolved.values) {
      if (!seen.has(value.fieldId)) {
        seen.add(value.fieldId);
        fieldIds.push(value.fieldId);
        fieldNames.push(value.field.name);
        fieldTypes.push(value.field.type);
      }
    }

    return { fieldIds, fieldNames, fieldTypes };
  }

  return { fieldIds: [], fieldNames: [], fieldTypes: [] };
}

/**
 * Provides the delete fields action using the selections API instead of redux.
 * Shows a confirmation dialog before deleting the selected fields.
 * Supports both column mode (direct field selection) and cell mode
 * (fields inferred from selected cell values).
 */
export const useDeleteFieldsAction = ({
  selectionsApi,
  panelManager,
  contentId
}: UseDeleteFieldsActionProps) => {
  const client = useAnalytics();
  const { formatMessage } = useIntl();
  const { showDialog } = useDialog();

  const handleDeleteFields = useCallback(() => {
    const resolved = selectionsApi.getResolvedSelection();

    if (resolved.mode !== 'column' && resolved.mode !== 'cell') {
      return;
    }

    const { fieldIds, fieldNames, fieldTypes } = resolveSelectedFieldInfo(resolved);

    if (fieldIds.length === 0) {
      return;
    }

    const panelData = panelManager.getData('FieldSettingsPanel');
    const panelFieldId = panelData?.fieldId;

    showDialog('ConfirmDialog', {
      heading: formatMessage(messages['field-settings.delete-field']),
      body: (
        <Stack space="space.150">
          <Box>
            {formatMessage(
              messages[
                'field-settings.delete-multiple-fields-confirmation-message.fields'
              ],
              {
                fieldsNumber: fieldNames.length,
                fields: <Text weight="bold">{fieldNames.join(', ')}</Text>
              }
            )}
          </Box>
          <Box>
            {formatMessage(
              messages[
                'field-settings.delete-multiple-fields-confirmation-message.additional'
              ]
            )}
          </Box>
        </Stack>
      ),
      yesLabel: formatMessage(messages['delete-buttons.delete-field-button']),
      noLabel: formatMessage(messages['delete-buttons.delete-field-cancel-button']),
      appearance: 'danger',
      onClose: (result: boolean) => {
        selectionsApi.clearPreviewIntent();

        if (result) {
          const reliability = ReliabilityMetrics.getInstance();

          try {
            if (panelFieldId && fieldIds.includes(panelFieldId)) {
              panelManager.hidePanel();
            }

            fieldIds.forEach((fieldId, index) => {
              void client.sendDeleteFieldEvent({
                contentId,
                databaseContainer: getDatabaseContainerType(),
                fieldIdDeleted: fieldId,
                fieldName: fieldNames[index],
                fieldType: fieldTypes[index]!
              });
            });

            traceDatabaseAction(
              {
                action: DatabaseTraceAction.FieldDelete,
                source: DatabaseTraceSource.ActionBar
              },
              () => {
                appStore.dispatch(doDeleteFieldsWithIds(contentId, fieldIds));
              }
            );
            selectionsApi.deselectAll();
            reliability.success(MetricName.DATABASE_DELETE_FIELD);
          } catch (e) {
            const errorMessage = e instanceof Error ? e.message : String(e);
            reliability.failure(MetricName.DATABASE_DELETE_FIELD, {
              errorMessage
            });
            throw e;
          }
        }
      }
    });
  }, [client, contentId, formatMessage, panelManager, selectionsApi, showDialog]);

  return handleDeleteFields;
};
